import { useState, useEffect } from "react";
import { ChevronLeft, ListMusic } from "lucide-react";
import { useAudio } from "../hooks/useAudio";
import { STREAMS, type StreamId } from "../constants";
import { formatDuration, pushToJukebox, isAdvert } from "../utils";
import { Visualizer } from "./Visualizer";
import { TrackItem } from "./TrackItem";
import type { Track, Album, JukeboxStatus, PushResult } from "../types";

interface StreamViewProps {
  id: StreamId;
  onBack: () => void;
}

export function StreamView({ id, onBack }: StreamViewProps) {
  const { state, selectStream, togglePlay, nowPlaying } = useAudio();
  const stream = STREAMS[id];
  const isCurrentStream = state.currentStream === id;
  const isPlaying = isCurrentStream && state.isPlaying;
  const [tracks, setTracks] = useState<Track[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [artworkImages, setArtworkImages] = useState<string[]>([stream.artwork]);
  const [currentArtIndex, setCurrentArtIndex] = useState(0);
  const [jukeboxStatus, setJukeboxStatus] = useState<JukeboxStatus | null>(null);

  const isJukebox = (stream as any).isJukebox === true;

  useEffect(() => {
    if (!isJukebox) return;

    const fetchStatus = () => {
      fetch("/api/jukebox/status")
        .then((r) => r.json())
        .then(setJukeboxStatus)
        .catch(() => {});
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [isJukebox]);

  const refreshStatus = async () => {
    try {
      const r = await fetch("/api/jukebox/status");
      const data = await r.json();
      setJukeboxStatus(data);
    } catch (e) {}
  };

  useEffect(() => {
    if (!isJukebox) return;
    const handler = () => refreshStatus();
    window.addEventListener("jukebox-refresh", handler);
    return () => window.removeEventListener("jukebox-refresh", handler);
  }, [isJukebox]);

  const handleQueueTrack = async (albumFolder: string, trackFilename: string): Promise<PushResult> => {
    const result = await pushToJukebox(albumFolder, trackFilename);
    await refreshStatus();
    return result;
  };

  useEffect(() => {
    if (!isCurrentStream) {
      selectStream(id);
    }
  }, [id]);

  useEffect(() => {
    if (stream.hasTrackList) {
      setLoading(true);
      fetch(`/api/tracks/${id}`)
        .then((r) => r.json())
        .then((data) => {
          setAlbums(data.albums || []);
          const allTracks = (data.albums || []).flatMap((a: Album) =>
            a.tracks.map(t => ({ ...t, _albumFolder: a.folder || "TEST-FOLDER-MISSING", _streamId: a.streamId }))
          );
          setTracks(allTracks);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [id]);

  const nowPlayingIsAd = isAdvert(nowPlaying?.albumFolder);

  const currentTrack = tracks.find((t) => {
    if (!nowPlaying?.title || nowPlayingIsAd) return false;
    const trackFolder = (t as any)._albumFolder;

    if (nowPlaying.albumFolder && nowPlaying.track && trackFolder) {
      return trackFolder === nowPlaying.albumFolder && t.track === parseInt(nowPlaying.track);
    }

    if (nowPlaying.albumFolder && trackFolder) {
      const titleMatch = t.title.includes(nowPlaying.title) || nowPlaying.title.includes(t.title);
      return titleMatch && trackFolder === nowPlaying.albumFolder;
    }

    return t.title.includes(nowPlaying.title) || nowPlaying.title.includes(t.title);
  });

  useEffect(() => {
    if (nowPlayingIsAd && nowPlaying?.adImages && nowPlaying.adImages.length > 0) {
      setArtworkImages(nowPlaying.adImages);
      setCurrentArtIndex(0);
    } else if (currentTrack) {
      const albumFolder = (currentTrack as any)._albumFolder;
      if (currentTrack.images.length > 0 && albumFolder) {
        // Use all images from the track's image_files array
        const images = currentTrack.images.map((img: string) => `/api/artwork/${albumFolder}/${img}`);
        setArtworkImages(images);
        setCurrentArtIndex(0);
      } else {
        // Fallback to stream artwork if no images in metadata or albumFolder is missing
        setArtworkImages([stream.artwork]);
        setCurrentArtIndex(0);
      }
    } else {
      setArtworkImages([stream.artwork]);
      setCurrentArtIndex(0);
    }
  }, [nowPlaying?.title, nowPlaying?.track, nowPlaying?.adImages, nowPlayingIsAd, currentTrack, id, albums, stream.artwork]);

  useEffect(() => {
    if (artworkImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentArtIndex((prev) => (prev + 1) % artworkImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [artworkImages.length]);

  const handlePageClick = () => {
    if (!isPlaying) {
      togglePlay();
    }
  };

  return (
    <div
      className="animate-[fade-in_0.3s_ease-out] min-h-screen pb-28 cursor-pointer"
      onClick={handlePageClick}
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-8 lg:px-12">
        <header className="py-6">
          <button
            onClick={(e) => { e.stopPropagation(); onBack(); }}
            className="flex items-center gap-2 font-mono text-xs tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <ChevronLeft size={14} />
            BACK TO STREAMS
          </button>
        </header>

        <main>
          <div className="flex flex-col items-center md:items-start md:flex-row gap-8 lg:gap-12 mb-12">
            <div
              className="w-64 h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-lg overflow-hidden flex-shrink-0 shadow-2xl relative"
              style={{ boxShadow: `0 25px 50px -12px ${stream.color}40` }}
            >
              {artworkImages.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={stream.name}
                  loading="eager"
                  decoding="sync"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    const fallbackImage = stream.fallbackImage || '/swansong.city.jpg';
                    if (!img.src.endsWith(fallbackImage)) {
                      img.src = fallbackImage;
                    }
                  }}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{
                    opacity: i === currentArtIndex ? 1 : 0.01,
                    transform: 'translateZ(0)',
                    WebkitTransform: 'translateZ(0)',
                  }}
                />
              ))}
            </div>

            <div className="flex-1 text-center md:text-left md:py-4">
              <div
                className="font-mono text-xs tracking-[0.2em] uppercase mb-3"
                style={{ color: stream.color }}
              >
                {stream.genre}
              </div>

              <h1
                className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[0.9] mb-4"
                style={{ color: stream.color }}
              >
                {stream.name}
              </h1>

              <p className="text-lg md:text-xl text-[var(--text-secondary)] mb-6">
                {stream.description}
              </p>

              <div className="flex items-center gap-4">
                <Visualizer color={stream.color} active={isPlaying} />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase">
                      Now Playing
                    </span>
                    {nowPlayingIsAd && (
                      <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                        AD
                      </span>
                    )}
                    {isJukebox && nowPlaying?.sourceType && (
                      <span
                        className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
                          nowPlaying.sourceType === "queue"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-blue-500/20 text-blue-400"
                        }`}
                      >
                        {nowPlaying.sourceType === "queue" ? "REQUESTED" : "AUTO"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {nowPlaying?.track && !nowPlayingIsAd && (
                      <span
                        className="font-mono text-sm"
                        style={{ color: stream.color }}
                      >
                        {nowPlaying.track.padStart(2, "0")}
                      </span>
                    )}
                    <span className="text-lg md:text-xl font-medium">
                      {nowPlayingIsAd
                        ? (nowPlaying?.title || "Ad Break")
                        : (nowPlaying?.title || "Loading...")}
                    </span>
                  </div>
                  {nowPlaying?.artist && (
                    <div className="text-[var(--text-secondary)]">
                      {nowPlaying.artist}
                    </div>
                  )}
                </div>
              </div>

              {isJukebox && jukeboxStatus && (
                <div className="mt-4 flex items-center gap-3">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)]"
                  >
                    <ListMusic size={14} style={{ color: stream.color }} />
                    <span className="font-mono text-sm">
                      {jukeboxStatus.currentSource === "queue"
                        ? (jukeboxStatus.totalQueued > 1
                          ? `playing, ${jukeboxStatus.totalQueued - 1} queued`
                          : "playing")
                        : (jukeboxStatus.totalQueued > 0
                          ? `${jukeboxStatus.totalQueued} queued`
                          : "empty")}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    Click + to request a track
                  </p>
                </div>
              )}
            </div>
          </div>

          {stream.hasTrackList && (
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-8 text-[var(--text-muted)]">
                  Loading tracks...
                </div>
              ) : albums.length > 0 ? (
                albums.map((album) => (
                  <div
                    key={album.folder}
                    className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg overflow-hidden"
                  >
                    {albums.length > 1 && (
                      <div
                        className="p-4 md:p-6 border-b border-[var(--border-subtle)] flex items-center gap-4"
                        style={{ borderLeftColor: album.color, borderLeftWidth: 4 }}
                      >
                        <img
                          src={album.artwork}
                          alt={album.name}
                          loading="eager"
                          className="w-16 h-16 rounded object-cover"
                        />
                        <div className="flex-1">
                          <h2 className="font-display text-xl" style={{ color: album.color }}>
                            {album.name}
                          </h2>
                          <p className="text-sm text-[var(--text-muted)]">
                            {album.tracks.length} tracks
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-[var(--text-muted)]">
                            {formatDuration(album.tracks.reduce((sum, t) => sum + t.duration, 0))}
                          </p>
                        </div>
                      </div>
                    )}
                    {albums.length === 1 && (
                      <div className="p-4 md:p-6 border-b border-[var(--border-subtle)] flex items-center justify-between">
                        <div>
                          <h2 className="font-display text-xl">Track List</h2>
                          <p className="text-sm text-[var(--text-muted)] mt-1">
                            {album.tracks.length} tracks
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-[var(--text-muted)]">
                            {formatDuration(album.tracks.reduce((sum, t) => sum + t.duration, 0))}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="p-2 md:p-4 space-y-1">
                      {album.tracks.map((track) => (
                        <TrackItem
                          key={`${album.folder}-${track.filename}`}
                          track={track}
                          streamId={album.streamId as StreamId}
                          albumFolder={album.folder}
                          isCurrentTrack={
                            currentTrack?.track === track.track &&
                            (currentTrack as any)?._albumFolder === album.folder
                          }
                          isPlaying={isPlaying}
                          color={albums.length > 1 ? album.color : stream.color}
                          showQueueButton={isJukebox}
                          onQueue={isJukebox ? handleQueueTrack : undefined}
                        />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-[var(--text-muted)]">
                  No tracks available
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
